import { i18n } from "../../../boot/i18n";
// Named export: the default export of src/store is the store FACTORY.
import { store } from "../../../store";
import LibraryFolder, { LIBRARY_TIERS } from "../../../classes/LibraryFolder";

const user = () => store.state.main.user;
const auth = () => Boolean(user() && user().isEmployee);
const adminAuth = () => Boolean(user() && user().isAdmin);

// The layout's breadcrumb is the page title (UI_STYLE_GUIDE, Content Pages),
// so the tier and folder chain render there rather than inside the page.
// Crumb labels and icons are functions of the route so they follow the
// store: Breadcrumbs.vue calls them with the target route's params merged in.
const folderOf = (route) =>
  store.state.main.libraryFolders[route.params.folderID] || null;
const tierOf = (route) => LIBRARY_TIERS[route.params.tier];
const tierTitle = (route) =>
  tierOf(route)
    ? i18n.global.t(tierOf(route).label)
    : i18n.global.t("Document Library");
const tierIcon = (route) => (tierOf(route) ? tierOf(route).icon : "library");
const folderTitle = (route) => {
  const folder = folderOf(route);
  return folder ? folder.data.name : i18n.global.t("Document Library");
};
const folderIcon = (route) => {
  const folder = folderOf(route);
  return folder && folder.isReadOnly ? "folder_read_only" : "folder";
};
// Document Library › tier › every ancestor of the folder being viewed.
const folderCrumbs = (route) => {
  const folder = folderOf(route);
  if (!folder) return ["library"];
  return [
    "library",
    { name: "library-tier", params: { tier: folder.data.tier } },
    ...folder.ancestors.map((ancestor) => ({
      name: "library-folder",
      params: { tier: folder.data.tier, folderID: ancestor.id },
    })),
  ];
};

export default {
  routes: [
    {
      // The drawer entry: its children are the tiers, so the entry itself is
      // only a group and lands on the first tier rather than duplicating it.
      name: "library",
      path: "library",
      redirect: { name: "library-tier", params: { tier: "public" } },
      meta: {
        icon: "library",
        title: i18n.global.t("Document Library"),
        auth,
      },
    },
    {
      name: "library-tier",
      path: "library/tier/:tier",
      meta: {
        icon: tierIcon,
        title: tierTitle,
        crumbs: ["library"],
        hideFromIndex: true,
        auth,
      },
      component: () => import("pages/employee/Library"),
    },
    {
      // Folders live under their tier's path so the drawer's tier entry stays
      // highlighted and expanded while browsing them.
      name: "library-folder",
      path: "library/tier/:tier/folder/:folderID",
      meta: {
        icon: folderIcon,
        title: folderTitle,
        crumbs: folderCrumbs,
        hideFromIndex: true,
        auth,
      },
      component: () => import("pages/employee/Library"),
    },
    {
      name: "library-trash",
      path: "library/trash",
      meta: {
        icon: "delete",
        title: i18n.global.t("Trash"),
        crumbs: ["library"],
        hideFromIndex: true,
        auth,
      },
      component: () => import("pages/employee/Library"),
    },
    {
      name: "library-activity",
      path: "library/activity",
      meta: {
        icon: "history",
        title: i18n.global.t("Activity Log"),
        crumbs: ["library"],
        hideFromIndex: true,
        auth: adminAuth,
      },
      component: () => import("pages/employee/LibraryActivity"),
    },
  ],

  dialogs: [
    {
      name: "library-folder-new",
      path: "/library/folder/new/:parentID",
      meta: {
        icon: "folder_add",
        title: i18n.global.t("New Library Folder"),
        auth: () => Boolean(user() && LibraryFolder.canBeCreatedBy(user())),
      },
      component: () => import("../../../dialogs/employee/LibraryFolderEdit"),
    },
    {
      name: "library-folder-edit",
      path: "/library/folder/:folderID/edit",
      meta: {
        icon: "folder",
        title: i18n.global.t("Edit Folder"),
        auth,
      },
      component: () => import("../../../dialogs/employee/LibraryFolderEdit"),
    },
    {
      name: "library-file-view",
      path: "/library/file/:fileID/view",
      meta: {
        icon: "file",
        title: i18n.global.tc("File"),
        auth,
      },
      component: () => import("../../../dialogs/employee/LibraryFileView"),
    },
    {
      // The activity log is admin-only in firestore.rules, so the per-file
      // history that reads it is too.
      name: "library-file-history",
      path: "/library/file/:fileID/history",
      meta: {
        icon: "history",
        title: i18n.global.t("History"),
        auth: adminAuth,
      },
      component: () => import("../../../dialogs/employee/LibraryFileHistory"),
    },
  ],
};
